import { Lightbulb } from 'lucide-react';
import { Badge, Card, CardHeader, CardTitle, CardBody, Chip } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { useContractSuggestions, type SampleContract } from '@/api/contracts';
import { formatDate } from '@/lib/format';

/* ─── helpers ─────────────────────────────────────────────────────────── */

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;

/** Map `contract_type` to a chip tone (SLA/NDA/Sample). */
function contractTypeTone(type?: string): 'success' | 'warning' | 'neutral' {
  if (!type) return 'neutral';
  const t = type.toLowerCase();
  if (t.includes('sla')) return 'success';
  if (t.includes('nda')) return 'warning';
  return 'neutral';
}

/* ─── columns ─────────────────────────────────────────────────────────── */

const columns: ColumnDef<SampleContract, unknown>[] = [
  {
    id: 'contract_number',
    header: 'Contract',
    cell: ({ row }) => {
      const r = row.original;
      const number = r.contract_number_id || `#${r.contract_id}`;
      const key = r.contract_key;
      return (
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)', gridTemplateColumns: 'minmax(0, 1fr)' }}>
          <span
            style={{
              fontSize: 'var(--vn-font-size-sm)',
              fontWeight: 'var(--vn-font-weight-medium)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {number}
          </span>
          {key ? (
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>{key}</span>
          ) : null}
        </div>
      );
    },
  },
  {
    id: 'contract_type',
    header: 'Type',
    cell: ({ row }) => {
      const type = row.original.contract_type;
      if (!type) return <span style={muted}>—</span>;
      return (
        <Chip appearance="subtle" tone={contractTypeTone(type)} size="sm">
          {type}
        </Chip>
      );
    },
  },
  {
    id: 'product_group_name',
    header: 'Product group',
    cell: ({ row }) => {
      const name = row.original.product_group_name;
      if (!name) return <span style={muted}>—</span>;
      return (
        <Chip appearance="subtle" tone="neutral" size="sm">
          {name}
        </Chip>
      );
    },
  },
  {
    id: 'contract_note',
    header: 'Note',
    cell: ({ row }) => {
      const note = row.original.contract_note;
      if (!note) return <span style={muted}>—</span>;
      return (
        <span
          style={{
            fontSize: 'var(--vn-font-size-sm)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          title={note}
        >
          {note}
        </span>
      );
    },
  },
  {
    id: 'attachments',
    header: 'Attachments',
    cell: ({ row }) => {
      const atts = row.original.attachments ?? [];
      if (!atts.length) return <span style={muted}>—</span>;
      const shown = atts.slice(0, 2);
      const overflow = atts.length - shown.length;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
          {shown.map((att, i) => (
            <Chip key={i} appearance="subtle" tone="neutral" size="sm">
              {att.attachment_file_name || att.attachment_name || `File ${i + 1}`}
            </Chip>
          ))}
          {overflow > 0 ? (
            <Badge tone="neutral" size="sm">+{overflow}</Badge>
          ) : null}
        </div>
      );
    },
  },
  {
    id: 'updated_at',
    header: () => <span style={{ display: 'block', textAlign: 'right' }}>Updated</span>,
    cell: ({ row }) => (
      <span style={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        {formatDate(row.original.updated_at)}
      </span>
    ),
  },
];

/* ─── component ───────────────────────────────────────────────────────── */

interface ContractSuggestionsSectionProps {
  id: number;
}

/**
 * Inline suggestions section — rendered only when the contract status is
 * `pending`. Fetches sample contracts from
 * `GET /contracts/customers/:id/suggestions` and shows them in a DataTable.
 * Mirrors the flat-Card section slot used by `ContractUpdatesLog`.
 */
export function ContractSuggestionsSection({ id }: ContractSuggestionsSectionProps) {
  const { data, isLoading, isError, refetch } = useContractSuggestions(id);
  const rows = data?.results ?? [];

  return (
    <Card flat>
      <CardHeader>
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <Lightbulb size={15} />
          Suggestions
        </CardTitle>
      </CardHeader>
      <CardBody style={{ padding: 0 }}>
        {isError ? (
          <div style={{ padding: 'var(--vn-space-4)' }}>
            <TabError title="Failed to load suggestions" onRetry={() => refetch()} />
          </div>
        ) : isLoading ? (
          <div style={{ padding: 'var(--vn-space-4)' }}>
            <TabLoading rows={3} />
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(row) => String(row.contract_id)}
            emptyState={
              <EmptyState
                icon={<Lightbulb size={24} />}
                title="No suggestions"
                description="No sample contracts were found for this contract."
              />
            }
          />
        )}
      </CardBody>
    </Card>
  );
}
